import React, { ReactNode, useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import SuperStepDot, { SuperStepState } from "./SuperStepDot";
import { SubStepType } from "./SubStep";
import { calculateStepState } from "./utils";
import VerticalStepItem from "./VerticalStepItem";

export interface Step {
  onClick?: (params: { index: number; isComplete: boolean }) => undefined;
  stepIndex: number;
  shortName: string;
  longName: string;
  subSteps: SubStepType[];
}

export interface StepperProps {
  activeStepIndex: number;
  greeting?: ReactNode;
  headerShown?: boolean;
  showMobileGreeting?: boolean;
  steps: Step[];
}

export interface StepItemProps {
  menuItemIndex: number;
  stepIndex: number;
  label: string;
  activeStepIndex: number;
  subSteps?: SubStepType[];
}

const Stepper: React.FC<StepperProps> = ({
  activeStepIndex,
  greeting,
  headerShown = false,
  showMobileGreeting = false,
  steps = []
}) => {
  const horizonalListItemRefs = useRef([]);
  const theme = useTheme();
  const screenWidthIsLgOrSmaller = useMediaQuery(theme.breakpoints.down("lg"));

  const renderVerticalSteps = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        position="relative"
      >
        <Box
          sx={{
            position: "absolute",
            top: 30,
            bottom: 60,
            left: "19px",
            borderLeft: `2px dashed ${COLORS.ACCENT2}`
          }}
        />
        {steps.map((step, index) => {
          return (
            <VerticalStepItem
              menuItemIndex={index}
              activeStepIndex={activeStepIndex}
              key={step.stepIndex}
              step={step}
            />
          );
        })}
      </Box>
    );
  };

  const renderFullVerticalStepper = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "30px",
          paddingLeft: "50px"
        }}
      >
        {!!greeting && greeting}
        {renderVerticalSteps()}
      </Box>
    );
  };

  const renderHorizontalStepper = () => {
    return (
      <Box
        component="ul"
        sx={{
          alignItems: "center",
          alignSelf: "flex-start",
          backgroundColor: COLORS.WHITE,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          boxSizing: "border-box",
          display: "flex",
          flexGrow: 1,
          gap: "15px",
          height: "50px",
          left: 0,
          margin: 0,
          maxHeight: "50px",
          msOverflowStyle: "none",
          overflowX: "scroll",
          overflowY: "hidden",
          padding: "15px 15px 15px 10px",
          position: headerShown ? "sticky" : "fixed",
          scrollbarWidth: "none",
          top: 0,
          whiteSpace: "nowrap",
          width: "100%",
          zIndex: 1000
        }}
      >
        {steps.map((step, index) => {
          const { stepState, superStepColoration } = calculateStepState(
            step,
            activeStepIndex
          );
          const overriddenStepState = [
            SuperStepState.ChildIsActive,
            SuperStepState.Active
          ].includes(stepState)
            ? SuperStepState.Active
            : stepState;
          const { onClick } = step;
          const isComplete = activeStepIndex > step.stepIndex;

          return (
            <Box
              component="li"
              key={step.stepIndex}
              onClick={() => onClick?.({ index: step.stepIndex, isComplete })}
              ref={(element) => {
                if (horizonalListItemRefs && horizonalListItemRefs.current) {
                  horizonalListItemRefs.current[index] = element;
                }
              }}
              sx={{
                alignItems: "center",
                cursor: onClick && isComplete ? "pointer" : "default",
                display: "flex",
                flexGrow: 1,
                flexDirection: "row",
                position: "relative",
                width: "max-content"
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center"
                }}
              >
                <SuperStepDot state={overriddenStepState} index={index + 1} />
                <Typography
                  sx={{
                    typography: {
                      xs: `body_strong_xs_${superStepColoration}`,
                      lg: `body_strong_${superStepColoration}`
                    }
                  }}
                >
                  {step.shortName}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  useEffect(() => {
    if (
      screenWidthIsLgOrSmaller &&
      horizonalListItemRefs.current[activeStepIndex] &&
      activeStepIndex > 0
    ) {
      horizonalListItemRefs.current[activeStepIndex].scrollIntoView({
        behavior: "instant",
        block: "end",
        inline: "start" // Scroll to the left edge of the element
      });
    }
  }, [activeStepIndex, horizonalListItemRefs, screenWidthIsLgOrSmaller]);

  return !!screenWidthIsLgOrSmaller ? (
    <Box
      sx={{
        alignItems: "center",
        boxSizing: "border-box",
        display: "contents",
        flexDirection: "column",
        gap: "30px",
        width: "100%"
      }}
    >
      {renderHorizontalStepper()}
      {!!greeting && showMobileGreeting && greeting}
    </Box>
  ) : (
    renderFullVerticalStepper()
  );
};

export default Stepper;
